import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import Button from '#components/Elements/Button/Button';
import Icon from '#components/Elements/Icon/Icon';
import DropdownItem from './Dropdown.Item.js';
import DropdownDivider from './Dropdown.Divider.js';
import DropdownHeader from './Dropdown.Header.js';
import { checkVisibility } from '#components/Layouts/Modal/Modal/Card.Body';
import { TooltipContext } from '#modules/context/TooltipContext';
import styles from './Dropdown.scss';

const BACKSPACE_KEY = 8;
const ESCAPE_KEY = 27;

export default class Dropdown extends PureComponent {
  static Header = DropdownHeader;
  static Item = DropdownItem;
  static Divider = DropdownDivider;
  static contextType = TooltipContext;

  constructor(props) {
    super(props);

    this.state = {
      open: false,
      searchFilter: '',
    };
  }

  componentDidMount() {
    document.addEventListener('click', this.handleExternalClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleExternalClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  handleExternalClick = (event) => {
    const { open } = this.state;

    // Check if the click event is outside the dropdown component and not the clicked dropdown
    if (this.htmlElement && !this.htmlElement.contains(event.target) && open) {
      this.close();
    }
    event.stopPropagation();
  };

  close = () => {
    const { open } = this.state;
    const { onMenuOpen } = this.props;
    const { setTooltipDisabled } = this.context;
    if (open) {
      this.setState({ open: false });
      onMenuOpen?.(false);
      setTooltipDisabled?.(!open);
      document.removeEventListener('keydown', this.onKeydown);
    }
  };

  onKeydown = ({ key, keyCode }) => {
    const { searchFilter } = this.state;
    let newValue = '';
    switch (keyCode) {
      case ESCAPE_KEY:
        break;
      case BACKSPACE_KEY:
        newValue = searchFilter.slice(0, -1);
        break;
      default:
        if (key === undefined || key.length > 1) {
          return;
        }
        newValue = `${searchFilter}${key}`;
    }
    this.setState({
      searchFilter: newValue,
    });
  };

  resetSearchFilter = () =>
    this.setState({
      searchFilter: '',
    });

  toggle = () => {
    const { open } = this.state;
    const { onMenuOpen } = this.props;
    const { hideActiveTooltip, setTooltipDisabled } = this.context;

    if (!open) {
      // Explicitly hide active tooltips since evt.preventDefault below negates Tooltip event handling
      hideActiveTooltip?.();

      // Disable all tooltips for this dropdown list while it's open
      // preventing the list contents from creating new tooltips for the parent.
      setTooltipDisabled?.(!open);

      this.resetSearchFilter();
      this.setState({ open: true });
      onMenuOpen?.(true);
      document.addEventListener('keydown', this.onKeydown);
    } else {
      this.close();
    }
  };

  select = (value, childOnClick) => (evt) => {
    const { onChange } = this.props;
    if (childOnClick) {
      childOnClick(evt);
    }
    onChange?.(value);
    this.close();
  };

  render() {
    const { open, searchFilter } = this.state;
    const {
      id,
      value = null,
      color,
      size,
      align,
      children,
      onChange,
      onMenuOpen,
      className,
      buttonIcon = 'fa-solid fa-caret-down',
      defaultText,
      triggerComponent,
      buttonProps,
      staticButtonText,
      selectedPrefix,
      disabled,
      readOnly,
      buttonlike,
      iconOnly,
      filterable,
      ...props
    } = this.props;
    let current = null;
    let prefix = null;

    const childrenArray = React.Children.toArray(children);

    childrenArray.map((child) => {
      if (child.props.value === value) {
        current = child.props.children;
        prefix = selectedPrefix || child.props.prefix || null;
      }
    });

    const menuItems = childrenArray
      .filter(
        (child) =>
          !filterable ||
          !searchFilter ||
          `${child.props.filterValue || child.props.value}`
            .toLowerCase()
            .includes(searchFilter.toLowerCase()),
      )
      .map((child) => {
        return React.cloneElement(
          child.props.itemPreview || child,
          child.type.isDropDownItem
            ? {
                active: child.props.value === value,
                onClick: this.select(child.props.value, child.props.onClick),
              }
            : {},
        );
      });

    const noItemsFound = menuItems.length === 0 && searchFilter.trim() !== '';

    return (
      <div
        {...props}
        ref={(node) => {
          this.htmlElement = node;
          checkVisibility(node);
        }}
        className={classNames('dropdown', styles.dropdown, className, {
          'is-active': open,
          [`is-${align}`]: align,
          'is-readonly': readOnly,
        })}
      >
        {triggerComponent ? (
          <div
            className={styles.flexCenteredFullwidth}
            role="presentation"
            onClick={readOnly ? null : this.toggle}
          >
            {triggerComponent}
          </div>
        ) : (
          <div
            className={classNames('dropdown-trigger', className, {
              buttonlike: buttonlike,
            })}
            role="presentation"
            onClick={readOnly ? null : this.toggle}
          >
            <Button
              id={id}
              color={color}
              size={size}
              disabled={disabled || readOnly}
              icon={iconOnly}
              {...buttonProps}
            >
              {prefix}
              {!iconOnly && (
                <span>{staticButtonText || current || defaultText || ''}</span>
              )}
              {(iconOnly || !readOnly) && buttonIcon && (
                <Icon icon={buttonIcon} />
              )}
            </Button>
          </div>
        )}
        <div
          className={classNames('dropdown-menu', className)}
          id="dropdown-menu"
          role="menu"
        >
          <div className="dropdown-content">
            {noItemsFound ? (
              <Dropdown.Item disabled>
                <FormattedMessage
                  defaultMessage="No items found"
                  description="No filtered dropdown items found"
                />
              </Dropdown.Item>
            ) : (
              menuItems
            )}
          </div>
        </div>
      </div>
    );
  }
}
